import { useLazyQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { UserCheckResponse, useAuth } from '@teamexos/fit-shared';
import React from 'react';
import { Title } from 'react-admin';
import { Controller, useForm } from 'react-hook-form';
import {
  AdminGetUserByEmailDocument,
  AdminGetUserByEmailQuery,
  AdminListEligibleEmailsDocument,
  AdminListEligibleEmailsQuery,
} from '../../graphql/types';
import { ResourceLink } from '../reusable/ResourceLink';

interface FormFields {
  email: string;
}

export const EmailDebugger = () => {
  const [loading, setLoading] = React.useState(false);

  const [userCheckResponse, setUserCheckResponse] =
    React.useState<UserCheckResponse>();
  const [userCheckError, setUserCheckError] = React.useState<string | null>(
    null,
  );

  const [eligibleEmailError, setEligibleEmailError] = React.useState<
    string | null
  >(null);
  const [eligibleEmailData, setEligibleEmailData] =
    React.useState<AdminListEligibleEmailsQuery>();

  const [userEmailError, setUserEmailError] = React.useState<string | null>(
    null,
  );
  const [userData, setUserData] = React.useState<AdminGetUserByEmailQuery>();

  const { userCheck } = useAuth();
  const [fetchEligibleEmails] = useLazyQuery(AdminListEligibleEmailsDocument);
  const [fetchUser] = useLazyQuery(AdminGetUserByEmailDocument);

  const methods = useForm<FormFields>({ defaultValues: { email: '' } });

  const handleSubmit = async (values: FormFields) => {
    setLoading(true);

    setUserCheckError(null);
    setEligibleEmailError(null);
    setUserEmailError(null);

    setEligibleEmailData(undefined);
    setUserCheckResponse(undefined);
    setUserData(undefined);

    if (!values.email) {
      setUserCheckError('Email is required');
      setLoading(false);
      return;
    }

    try {
      const info = await userCheck(values.email, true);
      setUserCheckResponse(info);
    } catch (error) {
      setUserCheckError(
        error instanceof Error ? error.message : 'An unknown error occurred',
      );
    }

    try {
      const eligibleEmails = await fetchEligibleEmails({
        variables: {
          filter: {
            email: values.email,
          },
        },
      });
      setEligibleEmailData(eligibleEmails.data);
    } catch (error) {
      setEligibleEmailError(
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while fetching eligible emails',
      );
    }

    try {
      const user = await fetchUser({
        variables: {
          email: values.email,
        },
      });
      setUserData(user.data);
    } catch (error) {
      setUserEmailError(
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while fetching user by email',
      );
    }

    setLoading(false);
  };

  return (
    <Box marginTop={2}>
      <Title title="Email Debugger" />
      <Typography variant="h3">Email Debugger</Typography>
      <Paper sx={{ marginTop: '1rem', padding: '1rem' }}>
        <Typography variant="body1">
          Enter an email address below to retrieve known information about the
          email address.
        </Typography>
        <Box marginTop={2}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(handleSubmit)}
            display="flex"
            flexDirection="column"
            alignItems="start"
            width="40%"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Controller
              control={methods.control}
              name="email"
              render={({ field }) => (
                <TextField
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  name={field.name}
                  value={field.value}
                  fullWidth
                  label="Email"
                />
              )}
            />
            <Button
              variant="contained"
              type="submit"
              sx={{ width: '100%' }}
              disabled={loading || !methods.formState.isValid}
            >
              {loading ? 'Loading...' : 'Check Email'}
            </Button>
          </Box>
          {!loading && (
            <Box>
              {userCheckError && (
                <Typography color="error">Error: {userCheckError}</Typography>
              )}
              {eligibleEmailError && (
                <Typography color="error">
                  Error fetching eligible emails: {eligibleEmailError}
                </Typography>
              )}
              {userEmailError && (
                <Typography color="error">
                  Error fetching user by email: {userEmailError}
                </Typography>
              )}
              {userCheckResponse && (
                <Box>
                  <Typography variant="h4">Basic Info</Typography>
                  {!userCheckResponse.registrationEnabled &&
                    userCheckResponse.registered && (
                      <Typography color="error">
                        This email address is registered but not eligible for
                        registration.
                      </Typography>
                    )}
                  {!userCheckResponse.registrationEnabled && (
                    <Typography color="error">
                      Email address is not eligible for registration.
                    </Typography>
                  )}
                  {userCheckResponse.registrationEnabled && (
                    <>
                      <Typography>
                        <strong>Eligible for registration:</strong>{' '}
                        {userCheckResponse.registrationEnabled ? 'Yes' : 'No'}
                      </Typography>
                      <Typography>
                        <strong>Has registered:</strong>{' '}
                        {userCheckResponse.registered ? 'Yes' : 'No'}
                      </Typography>
                      {!userCheckResponse.registered &&
                        userCheckResponse.hasCognitoUser && (
                          <Typography color="error">
                            A user with this email address exists in Cognito but
                            not in our database!
                          </Typography>
                        )}
                      {userCheckResponse.registered && (
                        <Typography>
                          <strong>User is active:</strong>{' '}
                          {userCheckResponse.active ? 'Yes' : 'No'}
                        </Typography>
                      )}
                      <Typography>
                        <strong>Currently eligible via client:</strong>{' '}
                        {userCheckResponse.client.name}
                      </Typography>
                      <Typography>
                        <strong>SSO enabled:</strong>{' '}
                        {userCheckResponse.isSSO ? 'Yes' : 'No'}
                      </Typography>
                      {userCheckResponse.isSSO && (
                        <Typography>
                          <strong>SSO Provider:</strong>{' '}
                          {userCheckResponse.identity.provider} /{' '}
                          {userCheckResponse.identity.connection}
                        </Typography>
                      )}
                      {!userCheckResponse.isSSO &&
                        userCheckResponse.registered && (
                          <>
                            <Typography>
                              <strong>Email verified:</strong>{' '}
                              {userCheckResponse.isEmailVerified ? 'Yes' : 'No'}
                            </Typography>
                            <Typography>
                              <strong>Password Reset Required:</strong>{' '}
                              {userCheckResponse.resetRequired ? 'Yes' : 'No'}
                            </Typography>
                          </>
                        )}
                    </>
                  )}
                </Box>
              )}
              {userData && (
                <Box marginTop={2}>
                  <Typography variant="h4">User Information</Typography>
                  {!userData.data?.id && (
                    <Typography color="error">
                      No user found with this email address.
                    </Typography>
                  )}
                  {userData.data?.id && (
                    <>
                      <Typography>
                        <strong>User ID:</strong> {userData.data.id}
                      </Typography>
                      <Typography>
                        <strong>Name:</strong>{' '}
                        <ResourceLink
                          name="User"
                          id={userData.data.id}
                          type="show"
                        >
                          {userData.data.firstName} {userData.data.lastName}
                        </ResourceLink>
                      </Typography>
                      {userData.data.fitClient ? (
                        <Typography>
                          <strong>Client:</strong>{' '}
                          <ResourceLink
                            name="Client"
                            id={userData.data.fitClient?.id}
                            type="show"
                          >
                            {userData.data.fitClient?.name || 'Unknown Client'}
                          </ResourceLink>
                        </Typography>
                      ) : (
                        <Box gap={2}>
                          <Typography>
                            <strong>Client:</strong> None
                          </Typography>
                          <Alert
                            variant="filled"
                            severity="error"
                            sx={{ padding: '0 1rem', width: '400px' }}
                          >
                            This user is not associated with any client. This
                            will render the app largely unusable for them.
                            <Box marginTop={1}>
                              If this user has reported issues with the app,
                              please contact engineering and let them know this
                              user needs to be associated with a client.
                            </Box>
                          </Alert>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
              {eligibleEmailData && eligibleEmailData.data.nodes.length > 0 && (
                <Box marginTop={2}>
                  <Typography variant="h4">Eligible Email Addresses</Typography>
                  {eligibleEmailData.data.nodes.map((email) => (
                    <div
                      key={email.id}
                      style={{ marginTop: '1rem', marginBottom: '1rem' }}
                    >
                      <Typography>
                        <strong>Email: </strong>
                        <ResourceLink
                          name="EligibleEmailAddress"
                          id={email.id}
                          type="show"
                        >
                          {email.email}
                        </ResourceLink>
                      </Typography>
                      {email.client && (
                        <Typography>
                          <strong>Client: </strong>
                          <ResourceLink
                            name="Client"
                            id={email.client?.id}
                            type="show"
                          >
                            {email.client?.name || 'Unknown Client'}
                          </ResourceLink>
                        </Typography>
                      )}
                      <Typography>
                        <strong>Archived:</strong>{' '}
                        {email.isArchived ? 'Yes' : 'No'}
                      </Typography>
                      <Typography>
                        <strong>App Eligible:</strong>{' '}
                        {email.isAppEligible ? 'Yes' : 'No'}
                      </Typography>
                      <Typography>
                        <strong>FCM Eligible:</strong>{' '}
                        {email.isFcmEligible ? 'Yes' : 'No'}
                      </Typography>
                    </div>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
